import React from 'react';
import { Box, Grid, Button } from '@mui/material';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { StyledTypography } from '../styles/styles';

// Placeholder image paths (replace with your actual images if available)
import placeholder2D from '../../../assets/images/healthycell2dholo.jpeg';
import placeholder3D from '../../../assets/images/healthycell3dholo.jpeg';
import placeholder3D2 from '../../../assets/images/thiscell3dholo.jpeg';
import placeholder2D2 from '../../../assets/images/thiscell2dholo.jpeg';
import placeholderChart from '../../../assets/images/cellspectralsignature.jpeg';

const brightRed = '#ff5630';
const textShadow = '0 0 8px #ff5630, 0 0 2px #fff';

const hologramImgStyle = {
  width: '100%',
  aspectRatio: '1 / 1',
  objectFit: 'cover',
  borderRadius: '1rem',
  border: '2px solid #e8442d',
  boxShadow: '0 4px 16px rgba(232,68,45,0.08)',
  marginBottom: 16,
  background: '#222',
  display: 'block',
  marginLeft: 'auto',
  marginRight: 'auto',
};

const CellHealthHologram = () => {
  const navigate = useNavigate();
  return (
    <Box sx={{
      background: 'linear-gradient(135deg, #000 0%, #1a1a1a 50%, #000 100%)',
      borderRadius: '2rem',
      p: 3,
      fontFamily: 'Bai Jamjuree',
      color: '#fff',
      height: '100vh',
      position: 'relative',
      border: '1px solid rgba(232, 68, 45, 0.2)',
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      boxSizing: 'border-box',
    }}>
      <Box sx={{ display: 'flex', flexDirection: 'row', flex: 1, minHeight: 0 }}>
        {/* Side Label */}
        <Box sx={{
          writingMode: 'vertical-rl',
          textOrientation: 'mixed',
          color: '#e8442d',
          fontWeight: 900,
          fontSize: '1.3rem',
          letterSpacing: 2,
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          justifyContent: 'center',
          mr: 3,
          ml: 1,
          textShadow: '0 0 8px #000',
        }}>
          Cell Health Hologram
        </Box>
        {/* Main Content Scrollable */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflowY: 'auto', pb: 10 }}>
          {/* Top: Columns for This Cell and 100% Healthy Cell */}
          <Grid container spacing={3} sx={{ mb: 2, flex: 1 }}>
            <Grid item xs={6} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <StyledTypography variant="subtitle1" align="center" sx={{ color: '#e8442d', fontWeight: 900, mb: 1, letterSpacing: 1, fontSize: '1.35rem' }}>
                This Cell
              </StyledTypography>
              <Box sx={{ width: '100%', flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box sx={{ width: '100%', aspectRatio: '1 / 1', mb: 2 }}>
                  <img src={placeholder3D2} alt="3D Hologram This Cell" style={{ ...hologramImgStyle, marginBottom: 0, height: '100%' }} />
                </Box>
                <Box sx={{ width: '100%', aspectRatio: '1 / 1' }}>
                  <img src={placeholder2D2} alt="2D Hologram This Cell" style={{ ...hologramImgStyle, marginBottom: 0, height: '100%' }} />
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <StyledTypography variant="subtitle1" align="center" sx={{ color: '#e8442d', fontWeight: 900, mb: 1, letterSpacing: 1, fontSize: '1.35rem' }}>
                100% Healthy Cell
              </StyledTypography>
              <Box sx={{ width: '100%', flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box sx={{ width: '100%', aspectRatio: '1 / 1', mb: 2 }}>
                  <img src={placeholder3D} alt="3D Hologram Healthy Cell" style={{ ...hologramImgStyle, marginBottom: 0, height: '100%' }} />
                </Box>
                <Box sx={{ width: '100%', aspectRatio: '1 / 1' }}>
                  <img src={placeholder2D} alt="2D Hologram Healthy Cell" style={{ ...hologramImgStyle, marginBottom: 0, height: '100%' }} />
                </Box>
              </Box>
            </Grid>
          </Grid>
          {/* Bottom: Spectral Signature Chart Placeholder */}
          <Box sx={{
            background: 'linear-gradient(135deg, #181818, #232323)',
            border: '2px solid #e8442d',
            borderRadius: '1rem',
            p: 2,
            mt: 2,
            boxShadow: '0 4px 16px rgba(232,68,45,0.08)',
          }}>
            <StyledTypography variant="subtitle2" align="center" sx={{ color: '#e8442d', fontWeight: 900, mb: 1, letterSpacing: 1, fontSize: '1.2rem' }}>
              Cell Spectral Signature
            </StyledTypography>
            <img src={placeholderChart} alt="Cell Spectral Signature" style={{ width: '100%', height: 180, objectFit: 'cover', borderRadius: '0.7rem', border: '1.5px solid #e8442d', background: '#222' }} />
            {/* Legend */}
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 3, mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 18, height: 4, borderRadius: 2, background: '#2196f3', mr: 1 }} />
                <StyledTypography variant="caption" sx={{ color: '#2196f3', fontWeight: 700 }}>This Cell</StyledTypography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 18, height: 4, borderRadius: 2, background: '#e8442d', mr: 1 }} />
                <StyledTypography variant="caption" sx={{ color: '#e8442d', fontWeight: 700 }}>100% Healthy Cell</StyledTypography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      {/* Navigation Buttons */}
      <Box sx={{
        position: 'fixed',
        left: 0,
        bottom: 0,
        width: '100vw',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        px: 3,
        py: 2,
        pointerEvents: 'none',
        zIndex: 1200,
      }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<ArrowBack />}
          sx={{
            background: 'linear-gradient(45deg, #e8442d, #ff6b35)',
            color: 'white',
            fontWeight: 'bold',
            fontSize: '1.1rem',
            borderRadius: '25px',
            px: 4,
            py: 1.5,
            boxShadow: '0 8px 25px rgba(232, 68, 45, 0.4)',
            border: '2px solid transparent',
            backgroundClip: 'padding-box',
            pointerEvents: 'auto',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 12px 35px rgba(232, 68, 45, 0.6)',
            },
          }}
          onClick={() => navigate(-1)}
        >
          Prev
        </Button>
        <Button
          variant="contained"
          color="primary"
          endIcon={<ArrowForward />}
          sx={{
            background: 'linear-gradient(45deg, #e8442d, #ff6b35)',
            color: 'white',
            fontWeight: 'bold',
            fontSize: '1.1rem',
            borderRadius: '25px',
            px: 4,
            py: 1.5,
            boxShadow: '0 8px 25px rgba(232, 68, 45, 0.4)',
            border: '2px solid transparent',
            backgroundClip: 'padding-box',
            pointerEvents: 'auto',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 12px 35px rgba(232, 68, 45, 0.6)',
            },
          }}
          onClick={() => navigate('/batteryscope-c/defect-analysis')}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
};

export default CellHealthHologram;
